import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import { Shapes } from '../StrokeIcons';
import type { Size } from './types';
import {
  sizeFont,
  sizeHeight,
  sizeIconClass,
  sizeInnerGap,
  sizePaddingL,
  sizePaddingR,
  sizeRounded,
} from './types';

export type RadioOption = {
  value: string;
  label: string;
  /**
   * Optional action (e.g. a delete button) rendered at the row's right, OUTSIDE the
   * selectable label — clicking it never changes the selection.
   */
  action?: ReactNode;
};

export type RadioProps = Omit<ComponentProps<'div'>, 'size' | 'onChange'> & {
  size?: Size;
  bordered?: boolean;
  error?: string;
  disabled?: boolean;
  name?: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
};

export default function Radio({
  size = 'md',
  bordered = true,
  error,
  disabled,
  name,
  options,
  value,
  onChange,
  className,
  ...props
}: RadioProps) {
  return (
    <div
      {...props}
      className={cn(
        'inline-flex flex-col overflow-hidden font-sans transition-colors divide-y',
        sizeRounded(size),
        sizeFont(size),
        'border',
        bordered && !error && 'border-slate-200 dark:border-charade-700 divide-slate-200 dark:divide-charade-700',
        bordered && error && 'border-fair-pink-300 dark:border-fair-pink-900 divide-fair-pink-300 dark:divide-fair-pink-900',
        !bordered && 'border-transparent',
        !disabled && !error && 'bg-white dark:bg-charade-950',
        !disabled && error && 'bg-fair-pink-100 dark:bg-fair-pink-950',
        disabled && 'bg-stone-50 dark:bg-slate-950',
        !disabled && !error && 'text-slate-600 dark:text-slate-100',
        disabled && 'text-stone-400 dark:text-slate-700',
        error && 'text-fair-pink-600 dark:text-fair-pink-400',
        className,
      )}>
      {options.map((opt) => {
        const checked = opt.value === value;
        // The row's height/hover/selection styling lives on a wrapper so an optional
        // action can sit beside the label without being part of the selectable area.
        return (
          <div
            key={opt.value}
            className={cn(
              'flex items-center transition-colors',
              sizeHeight(size),
              !disabled && !checked && 'hover:bg-haiti-100 dark:hover:bg-haiti-900',
              checked && 'bg-haiti-50 dark:bg-haiti-950',
            )}>
            <label
              className={cn(
                'inline-flex h-full min-w-0 flex-1 items-center cursor-pointer',
                sizeInnerGap(size),
                disabled && 'cursor-not-allowed',
              )}>
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={checked}
                onChange={() => onChange?.(opt.value)}
                disabled={disabled}
                className="hidden"
              />
              <span className={cn('shrink-0 flex items-center justify-center', sizePaddingL(size))}>
                <span className={cn(sizeIconClass(size), '*:size-full', checked && 'text-haiti-500')}>
                  {checked ? <Shapes.CircleWCircle /> : <Shapes.Circle />}
                </span>
              </span>
              <span className={cn('flex-1 min-w-0 truncate', sizePaddingR(size))}>{opt.label}</span>
            </label>
            {opt.action != null && (
              <span className={cn('shrink-0 flex items-center', sizePaddingR(size))}>{opt.action}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
